'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function TermsPage() {
  const lastUpdated = 'June 1, 2026';

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      content: `By accessing or using the Lenders Home LLC platform (the "Platform"), including any services, features, or content offered through the Platform, you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you must not use the Platform.

These Terms constitute a legally binding agreement between you and Lenders Home LLC ("Company," "we," "us," or "our"). We reserve the right to modify these Terms at any time. Continued use of the Platform following notice of changes constitutes acceptance of the revised Terms.

These Terms apply to all users of the Platform, including visitors, registered users, and loan applicants.`,
    },
    {
      id: 'eligibility',
      title: '2. Eligibility Requirements',
      content: `To use the Platform and submit a loan application, you must meet the following eligibility requirements:

• You must be at least 18 years of age.
• You must be a U.S. citizen or lawful permanent resident.
• You must have a valid Social Security Number.
• You must have a verifiable source of income.
• You must have an active U.S. bank account.
• You must not be currently involved in bankruptcy proceedings (certain programs may have exceptions).

By using the Platform, you represent and warrant that you meet all applicable eligibility requirements. Providing false or misleading information to circumvent eligibility requirements may constitute fraud and may be subject to legal action.`,
    },
    {
      id: 'responsibilities',
      title: '3. User Responsibilities',
      content: `As a user of the Platform, you agree to:

• Provide accurate, complete, and truthful information in all applications and communications.
• Promptly update any information that changes after submission.
• Maintain the confidentiality of your account credentials and notify us immediately of any unauthorized access.
• Use the Platform only for lawful purposes and in compliance with all applicable laws and regulations.
• Not attempt to circumvent, disable, or interfere with any security features of the Platform.
• Not use automated tools, bots, or scripts to access or interact with the Platform without prior written consent.
• Not submit applications on behalf of another person without proper legal authorization.

You are solely responsible for all activity that occurs under your account. Lenders Home LLC reserves the right to suspend or terminate accounts that violate these responsibilities.`,
    },
    {
      id: 'loan-process',
      title: '4. Loan Application Process',
      content: `Lenders Home LLC operates as a lending services platform that connects borrowers with lending programs. We are not a direct lender. By submitting a loan application through the Platform, you authorize us to:

• Collect and process your personal and financial information for the purpose of evaluating your application.
• Share your application information with lending program partners for the purpose of loan matching and underwriting.
• Conduct soft credit inquiries during the initial application phase.
• Conduct hard credit inquiries during the underwriting phase, with your explicit consent.

Submission of an application does not constitute a loan offer or guarantee of approval. All loan offers are subject to the terms and conditions of the specific lending program and lender.`,
    },
    {
      id: 'approval-disclaimer',
      title: '5. Approval Disclaimer',
      content: `LOAN APPROVAL IS NOT GUARANTEED. Submission of a loan application through the Platform does not guarantee approval. Approval decisions are made by lending program partners based on their individual underwriting criteria, which may include but are not limited to credit score, income verification, debt-to-income ratio, employment history, and collateral assessment.

Lenders Home LLC does not make approval decisions and is not responsible for the approval or denial decisions of lending program partners. We do not guarantee that any specific loan program will be available to any particular applicant.

Interest rates, loan amounts, and repayment terms presented during the application process are estimates only and are subject to change based on the final underwriting assessment.`,
    },
    {
      id: 'funding-disclaimer',
      title: '6. Funding Disclaimer',
      content: `FUNDING TIMELINES MAY VARY. Estimated funding timelines provided during the application process are approximations only. Actual funding times depend on factors including but not limited to the completeness of your application, the speed of document verification, the underwriting timeline of the lending program partner, and your bank's processing schedule.

Lenders Home LLC is not responsible for delays in funding caused by incomplete documentation, failed identity verification, banking system delays, or other factors outside our direct control.

Funds are disbursed by the lending program partner, not by Lenders Home LLC. Any disputes regarding funding amounts, timing, or terms must be directed to the lending program partner as identified in your loan agreement.`,
    },
    {
      id: 'liability',title: '7. Limitation of Liability',
      content: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, LENDERS HOME LLC AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM OR ANY LOAN OBTAINED THROUGH THE PLATFORM.

Our total liability to you for any claims arising out of or related to these Terms or the Platform shall not exceed the amount of fees paid by you to Lenders Home LLC in the twelve (12) months preceding the claim.

Nothing in these Terms limits our liability for fraud, gross negligence, or willful misconduct, or for any liability that cannot be excluded or limited under applicable law.`,
    },
    {
      id: 'governing-law',title: '8. Governing Law',
      content: `These Terms shall be governed by and construed in accordance with the laws of the United States and the state in which Lenders Home LLC is registered, without regard to conflict of law principles.

Any disputes arising out of or related to these Terms or the Platform shall be subject to the exclusive jurisdiction of the federal and state courts located in the applicable jurisdiction. You consent to personal jurisdiction in such courts.

If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.`,
    },
    {
      id: 'changes',title: '9. Changes to Terms',
      content: `Lenders Home LLC reserves the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Platform with a revised "Last Updated" date, and where appropriate, by sending an email notification to registered users.

Your continued use of the Platform after the effective date of any changes constitutes your acceptance of the revised Terms. If you do not agree to the revised Terms, you must discontinue use of the Platform.

We encourage you to review these Terms periodically to stay informed of any updates. The most current version of these Terms will always be available on the Platform.`,
    },
    {
      id: 'contact',title: '10. Contact Information',
      content: `For questions or concerns regarding these Terms and Conditions, please contact us:

Lenders Home LLC — Legal Department
Email: legal@lendershomellc.com
Mailing Address: Lenders Home LLC, Legal Department, United States
Response Time: Within 10 business days

For general support inquiries, visit our Contact Us page or email support@lendershomellc.com.`,
    },
  ];

  const [activeSection, setActiveSection] = useState(sections?.[0]?.id);

  return (
    <main className="min-h-screen bg-cloud">
      <Header />
      <section className="pt-28 pb-16 px-6 lg:px-10 bg-white border-b border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-light text-sky-dark text-xs font-semibold font-body mb-6 tracking-wide uppercase">
            Legal
          </div>
          <h1 className="font-heading text-display font-bold text-slate-dark leading-tight mb-4">Terms & Conditions</h1>
          <p className="text-slate-fog font-body">Last Updated: {lastUpdated} | Effective Date: {lastUpdated}</p>
          <p className="text-slate-fog font-body mt-3 max-w-3xl leading-relaxed">
            Please read these Terms and Conditions carefully before using the Lenders Home LLC platform. By accessing or using our services, you agree to be bound by these Terms.
          </p>
        </div>
      </section>
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-card border border-slate-mist/30 p-4 sticky top-24">
              <div className="text-xs font-semibold text-slate-fog font-body uppercase tracking-wide mb-3 px-2">Sections</div>
              <nav className="space-y-1">
                {sections?.map((s) => (
                  <button
                    key={s?.id}
                    onClick={() => setActiveSection(s?.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-body transition-all duration-200 ${
                      activeSection === s?.id
                        ? 'bg-sky text-white font-semibold' :'text-slate-fog hover:bg-cloud hover:text-slate'
                    }`}
                  >
                    {s?.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
          <div className="flex-1 min-w-0">
            {sections?.map((s) => (
              <div
                key={s?.id}
                id={s?.id}
                className={`bg-white rounded-3xl shadow-card border border-slate-mist/30 p-8 mb-6 ${activeSection === s?.id ? 'ring-2 ring-sky/20' : ''}`}
                onClick={() => setActiveSection(s?.id)}
              >
                <h2 className="font-heading text-xl font-bold text-slate-dark mb-5">{s?.title}</h2>
                <div className="text-sm text-slate-fog font-body leading-relaxed whitespace-pre-line">{s?.content}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
